import * as React from 'react'

import { Map as MapboxMap } from 'mapbox-gl'
import { useMap } from 'react-map-gl/mapbox'

import {
  groundColourAt,
  groundSignature,
  IGroundContext,
  SurfaceTone,
  toneForColour,
} from './labelGroundTone'
import { decidePlate, ICellReading, IPlateDecision } from './labelPlateDecision'
import {
  createRasterGroundReader,
  IGroundReadout,
  IRasterGroundReader,
  IRasterProbe,
} from './rasterGroundReader'
import { createMapboxRasterRuntime } from './rasterGroundRuntime'

// Where one label's glyphs sit: its own coordinate, plus a SCREEN offset in px, y positive
// down. A label never paints on its coordinate — a pin's hangs below it, a markup's is
// lifted above it — so the ground that decides its ink is not the ground under the anchor.
export interface ILabelAnchor {
  id: string
  longitude: number
  latitude: number
}

// The label's rendered text box, in screen px relative to its anchor: where the box's centre
// sits and how big it is. Screen-space, so the size does not move with zoom, but the offset
// does — a pin label's drop rides a zoom ramp — which is why the caller resolves it per
// settle rather than declaring it once.
export interface ILabelBoxPx {
  // Centre of the text box, offset from the anchor. Positive dy is down.
  dx: number
  dy: number
  width: number
  height: number
}

export type LabelBoxAt = (anchor: ILabelAnchor, zoom: number) => ILabelBoxPx

// The layer this hook's labels are drawn in, and the feature property carrying the anchor id.
// Given these, only labels mapbox actually placed are read: with thousands of labels in the
// data, collision means a few dozen reach the screen, and the ink of one that was never
// drawn is not a question anyone asked. Safe because the ink does not feed back into
// placement — text-color and halo take no part in collision — so this cannot oscillate.
export interface IRenderedLabels {
  // One id, or several when a tier's labels are split across layers — the location tier
  // splits by which parts an item displays, so its labels live in two.
  layerId: string | string[]
  idProperty: string
}

// One reader per map, not per hook: five label layers share a map here, and a reader each
// would fetch the same tile five times and memoise it five times over. Weak so it dies with
// the map — there is no other lifetime to hang it on.
const READERS = new WeakMap<MapboxMap, IRasterGroundReader | null>()
const NO_RASTERS: IGroundReadout = { rasters: new Map(), pending: new Set() }
// The label's box is tiled by cells and every cell sampled, so the reading describes the whole
// label rather than a slice: a column down the middle gave two labels on visibly different
// ground the same answer, and gave one straddling a boundary an answer describing neither side.
// Cost is not what keeps the grid small — the tile decode dominates and is paid once per tile.
const GRID_COLS = 5
const MAX_GRID_ROWS = 3
// How lopsided the vote has to be before a settled label is allowed to change ink. Below it
// the label keeps what it wears — a label split near evenly between two surfaces has no
// right answer, and re-tossing that coin every settle is what reads as flicker.
const DECISIVE_SHARE = 0.7

function decisive(votesLight: number, total: number): boolean {
  if (!total) return false
  return Math.max(votesLight, total - votesLight) / total >= DECISIVE_SHARE
}
// How far outside the viewport a label is still worth resolving, so panning it into view
// finds its ink already decided. Beyond this the projection stops describing anything: a
// point far off screen — or behind the camera at pitch — unprojects to ground the label will
// never stand on, and that answer would then be defended as if it had been observed.
const OFFSCREEN_MARGIN_PX = 256
// A ceiling on labels read in one settle, for the case where mapbox has not told us which
// ones it drew. Collision means only dozens ever reach the screen, so this never binds on a
// map that is rendering; it exists so a project with thousands of labels cannot turn one
// settle into a freeze while the layer is still coming up. Nearest the centre goes first,
// and anything past the cut keeps the ink it has rather than getting a wrong one.
const MAX_LABELS_PER_SETTLE = 600
// Long enough that a source's last tiles land in the same re-run as its first.
const SOURCE_SETTLE_MS = 250
// Long enough to swallow a drag at pointer rate, short enough that the ink a label lands on
// arrives before anyone reads it.
const ANCHOR_SETTLE_MS = 150

// A value that follows its input only once the input holds still. The first value passes
// straight through, so nothing waits on mount.
function useSettled<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = React.useState(value)
  React.useEffect(() => {
    if (settled === value) return undefined
    const timer = window.setTimeout(() => setSettled(value), delayMs)
    return () => window.clearTimeout(timer)
  }, [value, settled, delayMs])
  return settled
}

function readerFor(map: MapboxMap): IRasterGroundReader | null {
  if (!READERS.has(map)) {
    const runtime = createMapboxRasterRuntime(map)
    READERS.set(map, runtime ? createRasterGroundReader(runtime) : null)
  }
  return READERS.get(map) ?? null
}

// A geojson source can never qualify, which is what stops the label layer's own setData from
// re-entering this listener. Structural check, not a name blocklist.

// Narrow to what mapbox drew, then cap what is left. Null means "do not narrow", not "nothing
// is drawn". Ordered by GEOGRAPHIC distance: projecting thousands of anchors is the cost being
// avoided in the first place.
function boundedCandidates(
  map: MapboxMap,
  anchors: ILabelAnchor[],
  drawn: Set<string> | null,
): ILabelAnchor[] {
  const visible = drawn ? anchors.filter(a => drawn.has(a.id)) : anchors
  if (visible.length <= MAX_LABELS_PER_SETTLE) return visible
  const centre = map.getCenter()
  const distance = (a: ILabelAnchor): number => {
    const dx = a.longitude - centre.lng
    const dy = a.latitude - centre.lat
    return dx * dx + dy * dy
  }
  return [...visible]
    .sort((a, b) => distance(a) - distance(b))
    .slice(0, MAX_LABELS_PER_SETTLE)
}

function placedLabelIds(
  map: MapboxMap,
  rendered: IRenderedLabels | undefined,
): Set<string> | null {
  if (!rendered) return null
  try {
    const layers = (
      Array.isArray(rendered.layerId) ? rendered.layerId : [rendered.layerId]
    ).filter(id => map.getLayer(id))
    if (!layers.length) return null
    const features = map.queryRenderedFeatures({ layers })
    if (!features.length) return null
    const ids = new Set<string>()
    for (const feature of features) {
      const id = feature.properties?.[rendered.idProperty]
      if (typeof id === 'string') ids.add(id)
    }
    return ids.size ? ids : null
  } catch {
    return null
  }
}

function isGroundSource(map: MapboxMap, sourceId: string): boolean {
  try {
    const type = (map.getSource(sourceId) as { type?: string } | undefined)
      ?.type
    return type === 'raster' || type === 'image'
  } catch {
    return false
  }
}

function tonesEqual(
  a: Map<string, IPlateDecision>,
  b: Map<string, IPlateDecision>,
): boolean {
  if (a.size !== b.size) return false
  for (const [id, decision] of b) {
    const was = a.get(id)
    if (!was || was.tone !== decision.tone || was.plated !== decision.plated) {
      return false
    }
  }
  return true
}

// The ink each label wears, resolved from the scene — never from rendered pixels.
//
// The screen offset goes through the map's own projection: mercator arithmetic is only correct
// at pitch 0 / bearing 0. That makes the answer camera-dependent, so it runs on settle rather
// than per frame. Two phases — A answers synchronously from memory and decoded image sources,
// B fetches what A could not and applies it only if the camera has not moved on.
export function useLabelGroundTones(
  anchors: ILabelAnchor[],
  ground: IGroundContext,
  boxAt: LabelBoxAt,
  rendered?: IRenderedLabels,
  enabled = true,
  // Changes when a label can appear without moving, or the ground under the labels changes with
  // neither the camera nor a source saying so (a map file hidden): run() reads only the labels
  // mapbox placed, so it has to wait for the frame that places it.
  rerunKey?: string,
): Map<string, IPlateDecision> {
  const { current: mapRef } = useMap()
  // Tone and plate together, decided in one pass: they answer the same reading, and keeping
  // them in one map is what stops them ever describing different ground.
  const [tones, setTones] = React.useState<Map<string, IPlateDecision>>(
    () => new Map(),
  )

  // Latest inputs without rebinding the listener on every render.
  const anchorsRef = React.useRef(anchors)
  anchorsRef.current = anchors
  const groundRef = React.useRef(ground)
  groundRef.current = ground
  const boxAtRef = React.useRef(boxAt)
  boxAtRef.current = boxAt
  const renderedRef = React.useRef(rendered)
  renderedRef.current = rendered
  const rerunKeyRef = React.useRef(rerunKey)
  // Bumped per settle and on teardown; a resolve that loses the race applies nothing.
  const generationRef = React.useRef(0)
  // Tones decided on ground we had actually read — the only ones stickiness may defend.
  const settledRef = React.useRef(new Map<string, SurfaceTone>())

  // Triggers, not data: run() reads the refs above, so a late trigger still sees current
  // anchors. The anchor SET fires at once — a new label needs ink now — while POSITIONS settle
  // first, because ink is decided where a label lands, not while it is dragged across ground.
  //
  // Memoised on the anchors REFERENCE, and it has to be the array itself: a coarser dep like
  // anchors.length misses a position-only change, and the failure mode is a STALE tone rather
  // than a slow one.
  const idSignature = React.useMemo(
    () => anchors.map(a => a.id).join('|'),
    [anchors],
  )
  const positionSignature = React.useMemo(
    () =>
      anchors
        .map(a => `${a.longitude.toFixed(5)},${a.latitude.toFixed(5)}`)
        .join('|'),
    [anchors],
  )
  const settledPositions = useSettled(positionSignature, ANCHOR_SETTLE_MS)
  // Keyed on the FILLS ARRAY, never on `ground` itself: the wrapper is rebuilt per recompute,
  // while the array inside comes from a memo or computed in every host and holds still across
  // camera-only renders. Memoised for the same reason as the signatures above — five hosts would
  // otherwise redo this on every frame of a pan.
  const groundFills = ground.fills
  const groundKey = React.useMemo(
    () => groundSignature({ basemap: ground.basemap, fills: groundFills }),
    [ground.basemap, groundFills],
  )

  React.useEffect(() => {
    const map = mapRef?.getMap()
    if (!map || !enabled) return undefined

    // One probe per anchor per grid cell, keyed so the readout can be regrouped by label.
    const probeKey = (id: string, cellIndex: number): string =>
      `${id}@${cellIndex}`

    const apply = (
      anchors: ILabelAnchor[],
      probes: IRasterProbe[],
      cellsById: Map<string, number[]>,
      readout: IGroundReadout,
    ): void => {
      const byKey = new Map(probes.map(p => [p.key, p]))
      setTones(prev => {
        const next = new Map<string, IPlateDecision>()
        for (const anchor of anchors) {
          const steps = (cellsById.get(anchor.id) ?? [])
            .map(index => byKey.get(probeKey(anchor.id, index)))
            .filter(Boolean) as IRasterProbe[]
          // Off screen, so never probed: keep the ink it has and decide nothing. Seeding a
          // tone from a projection that describes no ground is how a stale answer survives.
          if (!steps.length) {
            const carried = prev.get(anchor.id)
            if (carried) next.set(anchor.id, carried)
            continue
          }
          // Still being read: hold the ink rather than show the basemap's guess for a frame
          // and correct it, because that correction is the blink. Holding cannot strand a
          // label — a source that loads re-runs us via sourcedata.
          const pending = steps.some(p => readout.pending.has(p.key))
          const held = prev.get(anchor.id)
          if (pending && held) {
            next.set(anchor.id, held)
            continue
          }
          // Vote per cell rather than averaging the luminances. An average across a boundary
          // invents a value that exists nowhere on the label: asphalt at 0.04 beside a roof
          // at 0.52 averages to 0.28, which reads as light ground and is wrong for both
          // halves. Counting which ink wins each cell answers the question actually being
          // asked — which ink is right for most of this label.
          let votesLight = 0
          const cells: ICellReading[] = []
          for (const p of steps) {
            const rasters = readout.rasters.get(p.key)
            const colour = groundColourAt(p.lng, p.lat, {
              ...groundRef.current,
              rasters,
            })
            const cellTone = toneForColour(colour)
            if (cellTone === 'light') votesLight++
            // Busiest layer in the stack, not the topmost. A stack is rare, and when there is
            // one, linework anywhere in it still runs between the glyphs — so this errs towards
            // plating, which is the safe direction to be wrong in.
            cells.push({
              tone: cellTone,
              contrast: (rasters ?? []).reduce(
                (worst, raster) => Math.max(worst, raster.contrast ?? 0),
                0,
              ),
            })
          }
          const majority: SurfaceTone =
            votesLight * 2 >= steps.length ? 'light' : 'dark'

          // Stickiness may only defend a tone that was decided on ground we had actually
          // read. Seeding it from the basemap placeholder would let the first guess win the
          // argument against the real answer that arrives a moment later.
          const settled = settledRef.current.get(anchor.id)
          const tone =
            settled && !decisive(votesLight, steps.length) ? settled : majority
          if (!pending) settledRef.current.set(anchor.id, tone)
          // Plate from the same cells that chose the ink. Passing the label's previous verdict
          // is what makes the hysteresis asymmetric: see decidePlate.
          const decision = decidePlate(cells, prev.get(anchor.id))
          next.set(anchor.id, {
            tone,
            plated: decision?.plated ?? prev.get(anchor.id)?.plated ?? false,
          })
        }
        // Hold the previous identity when nothing changed: consumers rebuild their GeoJSON
        // off this Map, and a fresh identity per settle would call setData for nothing.
        return tonesEqual(prev, next) ? prev : next
      })
    }

    const run = (): void => {
      const zoom = map.getZoom()
      const anchors = anchorsRef.current
      const canvas = map.getCanvas()
      const maxX = canvas.clientWidth + OFFSCREEN_MARGIN_PX
      const maxY = canvas.clientHeight + OFFSCREEN_MARGIN_PX
      // Labels mapbox placed this frame. Absent means it has not drawn the layer yet, in
      // which case fall back to the viewport test rather than deciding nothing.
      const drawn = placedLabelIds(map, renderedRef.current)
      const candidates = boundedCandidates(map, anchors, drawn)
      const probes: IRasterProbe[] = []
      const cellsById = new Map<string, number[]>()
      for (const anchor of candidates) {
        const at = map.project([anchor.longitude, anchor.latitude])
        if (
          !isFinite(at.x) ||
          !isFinite(at.y) ||
          at.x < -OFFSCREEN_MARGIN_PX ||
          at.y < -OFFSCREEN_MARGIN_PX ||
          at.x > maxX ||
          at.y > maxY
        ) {
          continue
        }
        const box = boxAtRef.current(anchor, zoom)
        const cols = box.width > 0 ? GRID_COLS : 1
        const cellWidth = box.width / cols
        // Cells stay roughly square, so a wide one-line label is sampled across rather than
        // down, and a narrow wrapped one is sampled down rather than across.
        const rows = Math.max(
          1,
          Math.min(
            MAX_GRID_ROWS,
            Math.round(box.height / Math.max(1, cellWidth)),
          ),
        )
        const cellHeight = box.height / rows
        const cells: number[] = []
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const dx = box.dx + (col + 0.5 - cols / 2) * cellWidth
            const dy = box.dy + (row + 0.5 - rows / 2) * cellHeight
            const glyphs = map.unproject([at.x + dx, at.y + dy])
            const index = cells.length
            cells.push(index)
            probes.push({
              key: probeKey(anchor.id, index),
              lng: glyphs.lng,
              lat: glyphs.lat,
              widthPx: cellWidth,
              heightPx: cellHeight,
            })
          }
        }
        cellsById.set(anchor.id, cells)
      }

      const reader = readerFor(map)
      if (!reader) {
        apply(anchors, probes, cellsById, NO_RASTERS)
        return
      }

      apply(anchors, probes, cellsById, reader.peek(probes, zoom))

      const generation = ++generationRef.current
      void reader.resolve(probes, zoom).then(readout => {
        // A settle that has been overtaken must not repaint the map behind the current one.
        if (generation === generationRef.current) {
          apply(anchors, probes, cellsById, readout)
        }
      })
    }

    // Fires on the source, not on the camera: an overlay that finishes loading after the
    // first settle would otherwise keep the ink it had when the ground was still blank.
    let rerunTimer: number | null = null

    const onSourceData = (event: {
      sourceId?: string
      isSourceLoaded?: boolean
    }): void => {
      if (!event.isSourceLoaded || !event.sourceId) return
      if (map.isMoving()) return
      if (!isGroundSource(map, event.sourceId)) return
      if (rerunTimer !== null) return
      rerunTimer = window.setTimeout(() => {
        rerunTimer = null
        run()
      }, SOURCE_SETTLE_MS)
    }

    const isRerun = rerunKeyRef.current !== rerunKey
    rerunKeyRef.current = rerunKey
    run()
    if (isRerun) map.once('idle', run)
    map.on('moveend', run)
    map.on('sourcedata', onSourceData)
    return () => {
      generationRef.current++
      if (rerunTimer !== null) window.clearTimeout(rerunTimer)
      map.off('idle', run)
      map.off('moveend', run)
      map.off('sourcedata', onSourceData)
    }
  }, [mapRef, idSignature, settledPositions, enabled, groundKey, rerunKey])

  return tones
}
